import { useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form/dist/index.ie11';
import { isEqual } from 'date-fns';
import { MonthAvailability } from 'src/components/oneCorp/features/MealCredit/common/formFields/mealCreditFormFields.utils';
import { IMealCreditFormFields } from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseManageMealCreditsPage.utils';
import { getCurrentMonthlyCycleEndDate } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/getCurrentMonthlyCycleEndDate';

export interface UseToDateHandler {
  cycleStartDate: Date;
  cycleEndDate: Date;
}

export const useToDateHandler = (name: string): UseToDateHandler => {
  const {
    watch,
    setValue,
    getValues,
    formState: { dirtyFields },
  } = useFormContext<IMealCreditFormFields>();
  const { date, monthAvailableOn } = watch(['date', 'monthAvailableOn']);

  const cycleEndDate = date
    ? getCurrentMonthlyCycleEndDate({
        firstInstanceStartDate: date,
        currentMonthlyCycleStartDate: date,
        // Use durationMinutes: 0 to calculate the end date of the monthly cycle.
        // We only need the date (day of the month)
        durationMinutes: 0,
      })
    : null;

  const handleSetValue = useCallback(() => {
    const value: Date = getValues(name);
    const isValueEqualToCycleEndDate = isEqual(value, cycleEndDate);
    if (
      !isValueEqualToCycleEndDate &&
      date &&
      monthAvailableOn === MonthAvailability.EVERY_DAY
    ) {
      setValue(name, cycleEndDate, { shouldValidate: true });
    }
  }, [date, monthAvailableOn, cycleEndDate, name, setValue, getValues]);

  const resetValue = useCallback(() => {
    const isSynchronizeWithDate = dirtyFields.date && date;
    if (
      isSynchronizeWithDate &&
      monthAvailableOn === MonthAvailability.WITHIN_DATE_RANGE
    ) {
      setValue(name, null, { shouldValidate: dirtyFields[name] });
    }
  }, [dirtyFields, date, name, setValue, monthAvailableOn]);

  useEffect(() => {
    handleSetValue();
  }, [handleSetValue]);

  useEffect(() => {
    resetValue();
  }, [resetValue]);

  return {
    cycleStartDate: date,
    cycleEndDate,
  };
};
